import { useMemo, useState } from 'react';
import { Button, Tooltip } from '@mui/material';

import { Icon } from '../shared/Icon';
import Pagination from '../shared/pagination/Pagination';
import { ChangeActiveSettingModal } from './ChangeActiveSettingModal';
import { PortalSettings } from './PortalSettings';
import { LabelInput, Loading } from '../shared';

import { PortalSettingsDTO, usePortalSettings } from '../hooks/portal';
import { useSearchParamState } from '../hooks/useSearchParamState';

import styles from './PortalSettingsLibrary.module.scss';

const PAGE_SIZE = 12;

interface PortalSettingsListProps {
    settings: PortalSettingsDTO[];
    allSettings: PortalSettingsDTO[];
    onSelectSetting: (id: string) => void;
    onCreateNew: () => void;
    onOpenChangeActive: () => void;
    searchValue: string;
    onSearchChange: (value: string) => void;
    currentPage: number;
    totalPageCount: number;
}

const PortalSettingsList = ({
    settings,
    allSettings,
    onSelectSetting,
    onCreateNew,
    onOpenChangeActive,
    searchValue,
    onSearchChange,
    currentPage,
    totalPageCount,
}: PortalSettingsListProps) => {
    return (
        <div className={styles.listContainer}>
            <menu className={styles.menu}>
                <LabelInput
                    value={searchValue}
                    label="Search"
                    size="normal"
                    onChange={(e) => onSearchChange(e.target.value)}
                />
                <div className={styles.createButtonWrapper}>
                    <Button
                        variant="outlined"
                        onClick={onOpenChangeActive}
                        className={styles.changeActiveButton}
                    >
                        Change Active Setting
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onCreateNew}
                        startIcon={<Icon name="add" />}
                    >
                        Create New Setting
                    </Button>
                </div>
            </menu>

            {settings.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>
                        {allSettings.length === 0
                            ? 'No portal settings found.'
                            : 'No settings match your search'}
                    </p>
                </div>
            ) : (
                <>
                    <div className={styles.settingsList}>
                        {settings.map((setting) => (
                            <div
                                key={setting.id}
                                className={`${styles.settingCard} ${
                                    setting.active ? styles.selected : ''
                                }`}
                                onClick={() => onSelectSetting(setting.id)}
                            >
                                <div className={styles.cardHeader}>
                                    <h3>{setting.name}</h3>
                                    <div className={styles.badges}>
                                        {setting.active && (
                                            <Tooltip
                                                title="Default setting for categories without a custom override"
                                                arrow
                                            >
                                                <span
                                                    className={`${styles.statusBadge} ${styles.active}`}
                                                >
                                                    Active
                                                </span>
                                            </Tooltip>
                                        )}
                                        {(setting.usedByCategories?.length ??
                                            0) > 0 && (
                                            <Tooltip
                                                title={`Used by: ${setting.usedByCategories!.map((c) => c.name).join(', ')}`}
                                                arrow
                                            >
                                                <span
                                                    className={`${styles.statusBadge} ${styles.custom}`}
                                                >
                                                    In use
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>
                                <div className={styles.cardContent}>
                                    <div className={styles.previewContainer}>
                                        <div
                                            className={styles.previewHeader}
                                            style={{
                                                backgroundColor: `rgba(${setting.headerColor.r * 255}, ${setting.headerColor.g * 255}, ${setting.headerColor.b * 255}, ${setting.headerColor.a})`,
                                            }}
                                        />
                                        <div
                                            className={styles.previewBody}
                                            style={{
                                                background:
                                                    setting.backgroundColors
                                                        .length === 1
                                                        ? `rgba(${setting.backgroundColors[0].r * 255}, ${setting.backgroundColors[0].g * 255}, ${setting.backgroundColors[0].b * 255}, ${setting.backgroundColors[0].a})`
                                                        : `linear-gradient(${
                                                              setting.gradientDirection ===
                                                              'vertical'
                                                                  ? 'to top'
                                                                  : 'to right'
                                                          }, ${setting.backgroundColors
                                                              .map(
                                                                  (color) =>
                                                                      `rgba(${color.r * 255}, ${color.g * 255}, ${color.b * 255}, ${color.a})`,
                                                              )
                                                              .join(', ')})`,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {totalPageCount > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPageCount={totalPageCount}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export const PortalSettingsLibrary = () => {
    const { data: settings, isLoading, setActiveSetting } = usePortalSettings();
    const [selectedSettingId, setSelectedSettingId] = useState<
        string | 'new' | null
    >(null);
    const [searchValue, setSearchValue] = useSearchParamState('', 'search');
    const [page] = useSearchParamState(1, 'page');
    const [isChangeActiveModalOpen, setIsChangeActiveModalOpen] =
        useState(false);

    const allSettings = settings ?? [];

    const handleSetActive = async (id: string) => {
        await setActiveSetting.mutateAsync({ id });
    };

    const filteredSettings = useMemo(() => {
        if (!searchValue) return allSettings;

        const normalizedSearch = searchValue.toLowerCase();
        return allSettings.filter(
            (setting) =>
                setting.name.toLowerCase().includes(normalizedSearch) ||
                setting.id.toLowerCase().includes(normalizedSearch),
        );
    }, [allSettings, searchValue]);

    const paginatedSettings = useMemo(() => {
        return filteredSettings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    }, [filteredSettings, page]);

    const onSearchUpdate = (value: string) => {
        setSearchValue(value, { page: 1 });
    };

    if (isLoading) {
        return <Loading />;
    }

    return (
        <div>
            {selectedSettingId ? (
                <PortalSettings
                    settingId={
                        selectedSettingId === 'new'
                            ? undefined
                            : selectedSettingId
                    }
                    onBack={() => setSelectedSettingId(null)}
                />
            ) : (
                <>
                    <PortalSettingsList
                        settings={paginatedSettings}
                        allSettings={allSettings}
                        onSelectSetting={setSelectedSettingId}
                        onCreateNew={() => setSelectedSettingId('new')}
                        onOpenChangeActive={() =>
                            setIsChangeActiveModalOpen(true)
                        }
                        searchValue={searchValue}
                        onSearchChange={onSearchUpdate}
                        currentPage={page}
                        totalPageCount={Math.ceil(
                            filteredSettings.length / PAGE_SIZE,
                        )}
                    />
                    <ChangeActiveSettingModal
                        open={isChangeActiveModalOpen}
                        onClose={() => setIsChangeActiveModalOpen(false)}
                        settings={allSettings}
                        onSetActive={handleSetActive}
                        isLoading={setActiveSetting.isPending}
                    />
                </>
            )}
        </div>
    );
};
